/**
 * Gated HITL bridge for `boolean_operation` (the fourth and final M1
 * mutating path).
 *
 * Flow: parse the model's tool input → `resolveBoolean` (every operand
 * resolved via the resolver; the ordered steps + `#k` refs validated) →
 * `previewBoolean` (the composite computed ONCE) → a {@link BooleanProposal}
 * the card renders with the ORDERED STEPS shown explicitly. The shared gate
 * (≤1-pending preamble, resolve-failure mapping, Apply/Reject wiring) lives in
 * {@link mutatingToolRunner}; this file holds only what's specific to a boolean
 * — the ordered steps input and the proposal's display fields. The steps are
 * carried VERBATIM onto the proposal (incl. `#k` refs) so the card shows the
 * exact ordered evaluation with no hidden precedence. Pure/React-free.
 */
import {
    resolveBoolean,
    previewBoolean,
    commitBoolean,
    OP_TO_ENGINE,
    type BooleanDeps,
    type BooleanInput,
    type BooleanStepInput,
} from './pipeline';
import {
    asRecord,
    failedPlan,
    gateProposal,
    mutatingPreamble,
    resolveFailureToPlan,
    type MutatingPlan,
    type MutatingToolCall,
    type MutationOutcome,
    type PendingFlag,
} from '../core';

export type BooleanToolCall = MutatingToolCall;

export interface BooleanPlanDeps extends BooleanDeps, PendingFlag {}

/** A step as the card renders it: the op + its operands in evaluation
 *  order, verbatim (an operand is an ROI name or a `#k` prior-step ref). */
export interface BooleanProposalStep {
    readonly op: BooleanStepInput['op'];
    readonly operands: ReadonlyArray<string>;
}

export interface BooleanProposal {
    readonly toolCallId: string;
    /** The ordered steps, exactly as proposed — shown explicitly on the
     *  card so there is no hidden precedence (ui_specs). */
    readonly steps: ReadonlyArray<BooleanProposalStep>;
    readonly newRoi: string;
    /** Previewed volume of the composite itself (cm³). */
    readonly compositeVolumeCm3: number;
    /** Composite minus first-operand volume (cm³) — for chaining. */
    readonly volumeDeltaCm3: number;
    /** Commit the ALREADY-previewed composite into a new structure. */
    apply(): Promise<MutationOutcome>;
    /** The clinician declined; the model is told and does not assume success. */
    reject(): MutationOutcome;
    showPreview(): void;
    hidePreview(): void;
}

export type BooleanPlan = MutatingPlan<BooleanProposal>;

// Single source: the valid op whitelist IS the locked op→engine map's
// keys, so the parser cannot drift from it.
const VALID_OPS: ReadonlySet<string> = new Set(Object.keys(OP_TO_ENGINE));

/** Defensively read the model-supplied (untyped) tool input: an ordered
 *  list of steps + newRoi. No formula field — steps are the only input. */
function readInput(input: unknown): BooleanInput | undefined {
    const o = asRecord(input);
    if (o === undefined) {
        return undefined;
    }
    if (typeof o.newRoi !== 'string' || !Array.isArray(o.steps)) {
        return undefined;
    }
    const steps: BooleanStepInput[] = [];
    for (const raw of o.steps) {
        const s = asRecord(raw);
        if (s === undefined) {
            return undefined;
        }
        if (
            typeof s.op !== 'string' ||
            !VALID_OPS.has(s.op) ||
            !Array.isArray(s.operands) ||
            !s.operands.every((x) => typeof x === 'string')
        ) {
            return undefined;
        }
        steps.push({
            op: s.op as BooleanStepInput['op'],
            operands: s.operands as string[],
        });
    }
    return { steps, newRoi: o.newRoi };
}

export async function planBooleanToolCall(
    call: BooleanToolCall,
    deps: BooleanPlanDeps
): Promise<BooleanPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }

    const parsed = readInput(call.input);
    if (parsed === undefined) {
        return failedPlan(call.toolCallId, 'invalid boolean_operation input');
    }

    const resolved = resolveBoolean(parsed, deps);
    if (resolved.kind !== 'validated') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }

    const preview = await previewBoolean(resolved.validated, deps);
    if (preview.kind === 'failed') {
        return failedPlan(call.toolCallId, preview.reason);
    }

    const previewed = preview.previewed;
    const proposal: BooleanProposal = {
        toolCallId: call.toolCallId,
        steps: parsed.steps.map((s) => ({ op: s.op, operands: s.operands })),
        newRoi: parsed.newRoi,
        compositeVolumeCm3: previewed.computed.resultVolumeCm3,
        volumeDeltaCm3: previewed.volumeDeltaCm3,
        ...gateProposal(previewed.computed, deps, () =>
            commitBoolean(previewed, deps)
        ),
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
